import { useMemo, useState } from "react";
import type { IssueDetail } from "../lib/segment-types";
import { renderPreview, type PreviewSection } from "../lib/newsletter-html";
import { EmailCanvas } from "./EmailCanvas";
import { categoryLabelAr } from "../lib/categories";
import { directionOf } from "../lib/text-direction";
import { Bidi } from "./primitives";

type Device = "desktop" | "mobile";
type Dir = "auto" | "rtl" | "ltr";

/**
 * The preview is the thing an editor looks at while writing, so it is the
 * right rail's primary content rather than a card buried under panels.
 *
 * Device and direction are separate axes. They were one toggle before, which
 * made "mobile in English" unreachable — and for an Arabic-heavy product that
 * mixes Latin medical terms, direction is not a single boolean.
 */
/** Set when the preview is the editing canvas rather than a read-only look. */
export interface CanvasControls {
  selectedId: string | null;
  dragging: boolean;
  onSelect: (id: string | null) => void;
  onDrop: (index: number) => void;
  onMove: (id: string, delta: -1 | 1) => void;
  onRemove: (id: string) => void;
  locked: boolean;
}

export function PreviewPanel({
  issue, sections, categories, previewCategory, onPreviewCategory, resolving, canvas,
}: {
  issue: IssueDetail;
  /** Sections with dynamic slots already resolved for `previewCategory`. */
  sections?: PreviewSection[];
  categories?: string[];
  previewCategory?: string | null;
  onPreviewCategory?: (c: string) => void;
  resolving?: boolean;
  canvas?: CanvasControls;
}) {
  const [device, setDevice] = useState<Device>("desktop");
  const [dir, setDir] = useState<Dir>("auto");
  const first = "سارة";
  const personalize = (s: string) => (s ?? "").replace(/\{\{\s*first_name\s*\}\}/gi, first);

  // "Auto" follows the content the way a mail client will.
  const bodyText = [
    issue.subject,
    issue.preheader,
    ...(issue.blocks ?? []).map((b) => b.text ?? ""),
    ...(sections ?? []).flatMap((x) => [
      x.heading ?? "", x.text ?? "",
      ...(x.articles ?? []).map((a) => a.title),
    ]),
  ].filter(Boolean).join(" ");
  const hasText = bodyText.trim().length > 0;
  const resolved: "rtl" | "ltr" = dir === "auto"
    ? (hasText && directionOf(bodyText) === "ltr" ? "ltr" : "rtl")
    : dir;

  const html = useMemo(
    () => renderPreview({
      title: issue.title,
      subject: issue.subject,
      preheader: issue.preheader,
      sender_name: issue.sender_name,
      reply_to: issue.reply_to,
      first_name: first,
      utm: issue.utm,
      articles: issue.articles_list ?? [],
      blocks: sections?.length ? undefined : issue.blocks,
      sections,
      category_heading: previewCategory ? categoryLabelAr(previewCategory) : undefined,
    }, { dir: resolved, editable: Boolean(canvas) }).html,
    [issue, resolved, sections, previewCategory, canvas],
  );

  const subject = personalize(issue.subject).trim();
  const preheader = issue.preheader?.trim() ?? "";

  // Placeholder copy only orients a draft nobody has touched yet. The moment
  // there is any content it goes: a line of invented text sitting in an inbox
  // preview reads as something that will be sent, and an empty preheader is
  // information — it means nothing follows the subject in the inbox.
  const started = Boolean(
    issue.title?.trim() || subject || preheader
    || (issue.blocks ?? []).some((b) => (b.text ?? "").trim() || (b.url ?? "").trim()),
  );

  return (
    <section className="nl-preview" aria-label="Email preview">
      {/* One slim row of view settings. They used to fill a card above the
          email — labels, a sentence about direction, the inbox — and pushed
          the thing being edited half a screen down. */}
      <div className="nl-canvas-bar">
        <div className="nl-segmented" role="group" aria-label="Device">
          <button type="button" aria-pressed={device === "desktop"} onClick={() => setDevice("desktop")}>Desktop</button>
          <button type="button" aria-pressed={device === "mobile"} onClick={() => setDevice("mobile")}>Mobile</button>
        </div>
        <div className="nl-segmented" role="group" aria-label="Text direction">
          <button type="button" aria-pressed={dir === "auto"} onClick={() => setDir("auto")}
            title={`Follow the content, as a mail client would — ${resolved === "rtl" ? "right-to-left" : "left-to-right"} now`}>
            Auto
          </button>
          <button type="button" aria-pressed={dir === "rtl"} onClick={() => setDir("rtl")}>RTL</button>
          <button type="button" aria-pressed={dir === "ltr"} onClick={() => setDir("ltr")}>LTR</button>
        </div>
        {/* With one category there is nothing to choose between. */}
        {(categories?.length ?? 0) > 1 && (
          <div className="nl-canvas-bar-as">
            <span>Preview as</span>
            <div className="nl-segmented" role="group" aria-label="Preview category">
              {categories!.map((c) => (
                <button key={c} type="button" aria-pressed={c === previewCategory} onClick={() => onPreviewCategory?.(c)}>
                  {categoryLabelAr(c)}
                </button>
              ))}
            </div>
          </div>
        )}
        {resolving && <span className="nl-canvas-bar-note">Resolving…</span>}
      </div>

      <div className={`nl-canvas-column${device === "mobile" ? " is-mobile" : ""}`}>
        {/* What the recipient sees before opening anything, at the email's width. */}
        <div className="nl-inbox" aria-label="Inbox preview">
          <div className="nl-inbox-from">{issue.sender_name || "Altibbi"}</div>
          {(subject || !started) && (
            <div className="nl-inbox-subject" data-placeholder={subject ? undefined : "true"}>
              <Bidi>{subject || "Subject line appears here"}</Bidi>
            </div>
          )}
          {(preheader || !started) && (
            <div className="nl-inbox-preheader" data-placeholder={preheader ? undefined : "true"}>
              <Bidi>{preheader || "Preheader text appears here"}</Bidi>
            </div>
          )}
        </div>

        {canvas ? (
          <EmailCanvas
            html={html}
            selectedId={canvas.selectedId}
            dragging={canvas.dragging}
            onSelect={canvas.onSelect}
            onDrop={canvas.onDrop}
            onMove={canvas.onMove}
            onRemove={canvas.onRemove}
            locked={canvas.locked}
          />
        ) : (
          <iframe
            className="nl-preview-frame nl-canvas-frame"
            title="Email body preview"
            srcDoc={html}
            sandbox="allow-popups allow-popups-to-escape-sandbox"
          />
        )}
      </div>
    </section>
  );
}
